import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import type { ProviderDetail } from '../../../../../js/api';
import { DocumentsService, ProvidersService, SourceTypeEnum } from '../../../../../js/api';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Badge } from '../../../components/badge';
import { DocumentsTab, DOCUMENTS_QUERY_KEY } from '../../../components/documents-tab';
import { DrawerCloseButton } from '../../../components/drawer';
import type { CodeDefinitionOption } from '../../../components/dropdown-select';
import { DropdownSelect } from '../../../components/dropdown-select';
import { ProviderRecordLinks } from '../../../components/provider-record-links';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsButton, TabsContent, TabsList } from '../../../components/tabs';
import { TooltippedText } from '../../../components/tooltipped-text';
import { WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { useCollapseOnOverflow } from '../../../hooks/useCollapseOnOverflow';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../../../utils/dataStyles';
import { staffProviderDetailPath } from '../../../utils/navigation';
import { CREDENTIAL_SECTIONS, IMMUNIZATION_SECTIONS } from './credentials';
import { EDUCATION_DOCUMENT_TYPES, EducationTab } from './EducationTab';
import { GeneralInfoTab } from './GeneralInfoTab';
import { ImmunizationsTab } from './ImmunizationsTab';
import { InsuranceTab } from './InsuranceTab';
import { LegacyUploadsSection } from './LegacyUploadsSection';
import { LicensesTab } from './LicensesTab';
import { countMissingRequiredCredentials } from './missingRequiredCredentials';
import { ProviderActionsMenu } from './ProviderActionsMenu';
import { ProviderActivity } from './ProviderActivity';
import {
  labelFor,
  PROVIDER_DRAWER_CATEGORIES,
  ProviderDrawerMaps,
  providerFileName,
} from './providerDisplay';
import { ProviderProfileSummary } from './ProviderProfileSummary';
import { ProviderQuickDetails } from './ProviderQuickDetails';
import {
  STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY,
  STAFF_PROVIDER_QUERY_KEY,
  STAFF_PROVIDER_REFERENCES_QUERY_KEY,
} from './queryKeys';
import { ReferencesTab } from './ReferencesTab';
import { useMissingRequiredDocuments } from './useMissingRequiredDocuments';

/** Tab codes double as the `tab` search param value. */
export const PROVIDER_DRAWER_TABS = [
  { code: 'general-info', label: 'General' },
  { code: 'references', label: 'References' },
  { code: 'education', label: 'Education' },
  { code: 'licenses', label: 'Licenses & Certs' },
  { code: 'insurance', label: 'Insurance' },
  { code: 'immunizations', label: 'Immunizations' },
  { code: 'documents', label: 'Documents' },
] as const;

type ProviderDrawerProps = {
  providerId: string;
  onClose?: () => void;
  /** Hide the popout button (e.g. when already on the full-page route). */
  isFullPage?: boolean;
};

type DrawerTab = { code: string; label: string; attention?: string };

/** Tooltip of the attention icon a tab carries while it has required gaps. */
const TAB_ATTENTION: Record<string, (missing: number) => string> = {
  education: (n) =>
    `${n} required ${n === 1 ? 'document type has' : 'document types have'} nothing on file`,
  licenses: (n) =>
    `${n} required ${n === 1 ? 'credential has' : 'credentials have'} no entry on file`,
  immunizations: (n) =>
    `${n} required ${n === 1 ? 'immunization has' : 'immunizations have'} no entry on file`,
};

/** The Documents tab shows its count; the credentialing tabs flag required gaps. */
function decorateTab(
  tab: DrawerTab,
  documentCount: number,
  missingByTab: Record<string, number>
): DrawerTab {
  if (tab.code === 'documents' && documentCount > 0) {
    return { ...tab, label: `Documents (${documentCount})` };
  }
  const missing = missingByTab[tab.code] ?? 0;
  const message = TAB_ATTENTION[tab.code];
  return missing > 0 && message ? { ...tab, attention: message(missing) } : tab;
}

export { staffProviderDetailPath };

/**
 * Staff view of a clinician: what the legacy Bootstrap profile page showed (header
 * info on top, the old profile tabs below), with documents and credential entries
 * managed through the documents framework. Uploads still only in the legacy models
 * are listed (and converted) in the Documents tab's Legacy uploads section.
 */
/**
 * Back-link label the record links hand to the target list: the full page
 * returns to this provider; the drawer returns to the Clinicians list (with
 * this drawer reopened via `?id=`).
 */
const recordLinksReferrerLabel = (provider: ProviderDetail, isFullPage: boolean) =>
  isFullPage ? provider.full_name || 'Clinician' : 'Clinicians';

export function ProviderDrawer({ providerId, onClose, isFullPage = false }: ProviderDrawerProps) {
  const { user } = useContext(SessionContext);
  // Django admin flag (not app-level staff): gates the admin link only.
  const showAdminLink = Boolean(user?.is_staff);
  const isMobile = useIsMobile();
  const drawerRef = useRef<HTMLDivElement>(null);
  // The tab lives in `?tab=` (default tab omitted) like the assignment/timecard/facility
  // drawers: deep-linkable, and sticky across providers because the list only swaps `id`.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const defaultTab = PROVIDER_DRAWER_TABS[0].code;
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState<string>(
    tabFromUrl && PROVIDER_DRAWER_TABS.some((t) => t.code === tabFromUrl) ? tabFromUrl : defaultTab
  );
  const setActiveTab = useCallback(
    (tab: string) => {
      setActiveTabState(tab);
      const next = new URLSearchParams(searchParams);
      if (tab && tab !== defaultTab) {
        next.set('tab', tab);
      } else {
        next.delete('tab');
      }
      // Keep the history state so the full page's referrer survives tab changes.
      setSearchParams(next, { replace: true, state: location.state });
    },
    [searchParams, setSearchParams, defaultTab, location.state]
  );

  const queryClient = useQueryClient();
  const {
    data: provider,
    isLoading,
    error,
  } = useQuery<ProviderDetail>({
    queryKey: [STAFF_PROVIDER_QUERY_KEY, providerId],
    queryFn: () => ProvidersService.providersRetrieve({ id: providerId }),
    enabled: !!providerId,
  });

  // Lightweight documents count for the Documents tab label (mirrors the
  // connection/assignment/timecard drawers).
  const { data: documentsData } = useQuery({
    queryKey: [STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, providerId],
    queryFn: () =>
      DocumentsService.documentsList({
        contextType: WorkflowScopes.Provider,
        contextId: providerId,
        limit: 1,
      }),
    enabled: !!providerId,
  });
  const documentCount = (documentsData as any)?.count ?? 0;

  const handleDocumentsChanged = () => {
    queryClient.invalidateQueries({
      queryKey: [STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, providerId],
    });
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, providerId] });
    // survey response PDFs are read through the references list
    queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_REFERENCES_QUERY_KEY, providerId] });
  };

  // Required education documents / credential entries with nothing on file put an
  // attention icon on their tab.
  const missingEducation = useMissingRequiredDocuments(providerId, EDUCATION_DOCUMENT_TYPES);
  const missingCredentials = countMissingRequiredCredentials(provider, CREDENTIAL_SECTIONS);
  const missingImmunizations = countMissingRequiredCredentials(provider, IMMUNIZATION_SECTIONS);

  const tabs = useMemo(
    () =>
      PROVIDER_DRAWER_TABS.map((t) =>
        decorateTab(t, documentCount, {
          education: missingEducation,
          licenses: missingCredentials,
          immunizations: missingImmunizations,
        })
      ),
    [documentCount, missingEducation, missingCredentials, missingImmunizations]
  );
  const { ref: tabsMeasureRef, collapsed: isOverflowCollapsed } =
    useCollapseOnOverflow<HTMLDivElement>({
      observeChildren: true,
      forceCollapse: isMobile,
      constraintRef: drawerRef,
      // The tabs only mount once the provider has loaded; flipping the id then makes the
      // hook (re)install its observers against the mounted strip.
      recheckOn: { id: provider ? providerId : '', tabs },
    });

  // A profile picture that fails to load (missing object, expired signed URL) falls back
  // to the placeholder icon instead of a broken image.
  const [pictureFailed, setPictureFailed] = useState(false);
  useEffect(() => {
    setPictureFailed(false);
  }, [providerId]);

  useDocumentTitle(provider?.full_name ?? '', false, isFullPage && !!provider?.full_name);

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps(PROVIDER_DRAWER_CATEGORIES);
  const maps: ProviderDrawerMaps = codeDefinitionMaps || {};

  if (isLoading) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8" data-testid="provider-drawer-loading">
        <div className="tw:text-zinc-500">Loading clinician details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8" data-testid="provider-drawer-error">
        <div className="tw:text-stoplight-danger">
          Error loading clinician details. Please try again.
        </div>
      </div>
    );
  }

  if (!provider) {
    return null;
  }

  const subtitle = [provider.public_name].filter(Boolean).join(' · ');

  return (
    // `@container`: the two-column grids in the strip, summary and tabs key off the
    // drawer's own width (`tw:@xl:`), not the viewport — side-by-side on a laptop
    // the drawer is far narrower than the `md:` breakpoint implies.
    <div
      ref={drawerRef}
      className="tw:@container tw:flex tw:flex-col tw:mt-6"
      data-testid="provider-drawer"
    >
      {/* Top Section - Provider Info, Status, Close */}
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:mb-4">
        {provider.profile_picture && !pictureFailed ? (
          <span className="tw:inline-block tw:size-12 tw:shrink-0 tw:overflow-hidden tw:rounded-lg tw:outline-1 tw:-outline-offset-1 tw:outline-zinc-300 tw:md:order-1">
            <img
              src={provider.profile_picture}
              alt={provider.full_name}
              className="tw:size-full tw:object-cover"
              onError={() => setPictureFailed(true)}
              data-testid="provider-drawer-picture"
            />
          </span>
        ) : (
          <div
            className="tw:hidden tw:md:flex tw:items-center tw:justify-center tw:size-12 tw:shrink-0 tw:bg-brand-green-1 tw:text-white tw:rounded-full tw:md:order-1"
            data-testid="provider-drawer-picture-fallback"
          >
            <i className="fa-solid fa-user-doctor tw:text-2xl" />
          </div>
        )}
        <div className="tw:flex tw:flex-col tw:gap-0.5 tw:flex-1 tw:min-w-0 tw:order-2">
          <div className="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
            <span
              className="tw:text-base tw:md:text-xl tw:font-bold tw:truncate"
              data-testid="provider-drawer-name"
            >
              {provider.full_name || provider.display_name}
            </span>
            {provider.source_type === SourceTypeEnum.ORGANIZATION && (
              <span className={SOURCE_TYPE_ORGANIZATION_BADGE_STYLE}>1099</span>
            )}
          </div>
          {subtitle && (
            <span
              className="tw:text-sm tw:text-zinc-500 tw:truncate"
              data-testid="provider-drawer-subtitle"
            >
              {subtitle}
            </span>
          )}
        </div>
        <div className="tw:order-4 tw:w-full tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <StatusPill scope={WorkflowScopes.Provider} status={provider.current_status} />
          <Badge color="zinc">
            {labelFor(provider.registration_status, maps.RegistrationStatus)}
          </Badge>
          {provider.profile_disposition && (
            <Badge color="zinc">
              {labelFor(provider.profile_disposition, maps.ProfileDisposition)}
            </Badge>
          )}
          {/* Spacer (not ml-auto on the links): right-aligns the record links when
              they share the badges line, but lets them start at the left once they wrap. */}
          <span className="tw:flex-1" aria-hidden="true" />
          <ProviderRecordLinks
            providerId={providerId}
            searchName={provider.display_name}
            isStaff
            referrerLabel={recordLinksReferrerLabel(provider, isFullPage)}
          />
        </div>
        {/* Actions menu + Popout + Close - right side of row 1 */}
        <div className="tw:order-3">
          <ProviderActionsMenu provider={provider} showAdminLink={showAdminLink} />
        </div>
        {!isFullPage && !isMobile && (
          <button
            type="button"
            className="tw:order-3 tw:text-zinc-400 tw:hover:text-zinc-600 tw:p-1"
            onClick={() =>
              navigate(
                staffProviderDetailPath(providerId, activeTab !== defaultTab ? activeTab : null),
                {
                  state: { referrer: location.pathname + location.search },
                }
              )
            }
            title="Open full page"
            data-testid="provider-drawer-popout"
          >
            <i className="fas fa-up-right-from-square" />
          </button>
        )}
        {onClose && (
          <DrawerCloseButton
            onClick={onClose}
            className="tw:order-3"
            data-testid="provider-drawer-close"
          />
        )}
      </div>

      <ProviderQuickDetails provider={provider} maps={maps} />

      <ProviderProfileSummary provider={provider} maps={maps} />

      <Tabs activeTab={activeTab} onChange={setActiveTab} className="tw:mt-6">
        <div className="tw:flex tw:flex-col tw:w-full">
          {isOverflowCollapsed ? (
            <div className="tw:pb-2">
              <div className="tw:text-xs tw:text-zinc-500 tw:mb-1">Select View</div>
              <DropdownSelect
                inputId="provider-drawer-section"
                aria-label="Clinician section"
                // the collapsed picker is plain text: mark a tab that needs attention
                allOptions={tabs.map((t) => ({
                  value: t.code,
                  label: t.attention ? `${t.label} ⚠` : t.label,
                }))}
                value={activeTab}
                isSearchable={false}
                isClearable={false}
                onChange={(opt: CodeDefinitionOption | CodeDefinitionOption[] | null) => {
                  if (opt && !Array.isArray(opt)) setActiveTab(opt.value);
                }}
              />
            </div>
          ) : null}
          <div
            aria-hidden={isOverflowCollapsed}
            ref={tabsMeasureRef}
            className={twMerge(
              'tw:w-full',
              isOverflowCollapsed &&
                'tw:invisible tw:max-h-0 tw:overflow-hidden tw:pointer-events-none'
            )}
          >
            <TabsList className={isOverflowCollapsed ? 'tw:border-transparent' : undefined}>
              {tabs.map((t) => (
                <TabsButton key={t.code} tab={t.code} dataTestId={`provider-tab-${t.code}`}>
                  {t.attention ? (
                    // One child with its own tight gap: TabsButton spaces its children
                    // with gap-2, which left the icon floating between two tab names.
                    <span className="tw:inline-flex tw:items-center tw:gap-1">
                      {t.label}
                      <TooltippedText tooltipContent={t.attention}>
                        <i
                          className="fas fa-triangle-exclamation tw:text-xs tw:text-amber-700"
                          aria-label={t.attention}
                          data-testid={`provider-tab-${t.code}-attention`}
                        />
                      </TooltippedText>
                    </span>
                  ) : (
                    t.label
                  )}
                </TabsButton>
              ))}
            </TabsList>
          </div>
        </div>

        <TabsContent tab="general-info">
          <GeneralInfoTab provider={provider} maps={maps} />
        </TabsContent>
        <TabsContent tab="references">
          <ReferencesTab provider={provider} maps={maps} />
        </TabsContent>
        <TabsContent tab="education">
          <EducationTab
            provider={provider}
            maps={maps}
            onOpenDocuments={() => setActiveTab('documents')}
          />
        </TabsContent>
        <TabsContent tab="licenses">
          <LicensesTab provider={provider} maps={maps} />
        </TabsContent>
        <TabsContent tab="insurance">
          <InsuranceTab
            provider={provider}
            maps={maps}
            onOpenDocuments={() => setActiveTab('documents')}
          />
        </TabsContent>
        <TabsContent tab="immunizations">
          <ImmunizationsTab provider={provider} maps={maps} />
        </TabsContent>
        <TabsContent tab="documents">
          <DocumentsTab
            contextType={WorkflowScopes.Provider}
            contextId={providerId}
            ownerId={providerId}
            relatedProviderId={providerId}
            onDocumentsChanged={handleDocumentsChanged}
            // A provider accumulates every credentialing attachment; group by type.
            groupByType
            // checkboxes + Download / Remove, like the credentialing tabs
            selectable={{ zipSubject: `${providerFileName(provider)} Documents` }}
            // one ⋮ menu per row; the name opens the document
          />
          {/* What is still only in the legacy models, until each file is converted. */}
          <LegacyUploadsSection
            provider={provider}
            maps={maps}
            onConverted={handleDocumentsChanged}
          />
        </TabsContent>
      </Tabs>

      <ProviderActivity providerId={providerId} />
    </div>
  );
}
